import {
  type ActionResult,
  type Calendar,
  type Post,
  planAllows,
  type Wall,
} from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, api, patch, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

const DAY = 86_400_000;
const parse = (d: string) => new Date(`${d}T12:00:00Z`).getTime();
const fmt = (d: string, o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { ...o, timeZone: "UTC" });

function NewSeason({
  calendar,
  walls,
  onClose,
}: {
  calendar: Calendar;
  walls: Wall[];
  onClose: () => void;
}) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const main = walls.filter((w) => w.kind === "normal" && w.status === "live");
  const [name, setName] = useState("Halloween");
  const [query, setQuery] = useState("Halloween");
  const [startsOn, setStarts] = useState(`${calendar.today.slice(0, 4)}-10-24`);
  const [endsOn, setEnds] = useState(`${calendar.today.slice(0, 4)}-10-31`);
  const [wallId, setWallId] = useState(main[0]?.id ?? "");
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const found = useQuery({
    queryKey: ["season-find", ws, query],
    queryFn: () =>
      api<{ label: string; posts: Post[]; liveCount: number; summary: string }>(
        `/v1/w/${ws}/seasons/find?season=${encodeURIComponent(query)}`,
      ),
    enabled: query.trim().length > 1,
  });
  const usable = (found.data?.posts ?? []).filter((p) => p.live);
  const chosen = picked ?? new Set(usable.map((p) => p.id));
  const page = main.find((w) => w.id === wallId);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<ActionResult>(`/v1/w/${ws}/seasons`, {
        name,
        postIds: [...chosen],
        replacesWallId: wallId,
        page: page?.pages[0],
        startsOn,
        endsOn,
      });
      toast(r.summary);
      await qc.invalidateQueries({ queryKey: ["schedules", ws] });
      await qc.invalidateQueries({ queryKey: ["walls", ws] });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-back" role="dialog" aria-modal="true" aria-label="New seasonal wall">
      <div className="modal">
        <h2 style={{ fontSize: 24 }}>New seasonal wall</h2>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Name</span>
          <input
            className="input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setQuery(e.target.value);
              setPicked(null);
            }}
          />
        </label>
        <div className="stack" style={{ gap: 8 }}>
          <div className="row">
            <span className="label spacer">Posts</span>
            <span className="small" style={{ color: "var(--ok)", fontWeight: 700 }}>
              {found.isFetching
                ? "Looking…"
                : `✦ Brandwall found ${found.data?.posts.length ?? 0} posts that match`}
            </span>
          </div>
          <div className="strip" role="group" aria-label="Posts for the seasonal wall">
            {(found.data?.posts ?? []).map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={chosen.has(p.id)}
                disabled={!p.live}
                title={p.live ? `@${p.creator}` : "Not ready to show yet"}
                onClick={() => {
                  const n = new Set(chosen);
                  n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                  setPicked(n);
                }}
                style={p.live ? {} : { opacity: 0.4 }}
              >
                {p.previewUrl ? (
                  <img src={p.previewUrl} alt={`Post by @${p.creator}`} />
                ) : (
                  <span style={{ display: "block", width: 64, height: 64 }} />
                )}
                {chosen.has(p.id) && <span className="n">✓</span>}
              </button>
            ))}
          </div>
          {found.data && found.data.posts.length > usable.length && (
            <p className="small muted">
              {found.data.posts.length - usable.length} matching posts aren't ready to show yet.
              Approve them in the Inbox to start asking their creators for permission.
            </p>
          )}
        </div>
        <div className="grid2" style={{ gap: 12 }}>
          <label className="field">
            <span className="label">Goes live</span>
            <input
              className="input"
              type="date"
              value={startsOn}
              onChange={(e) => setStarts(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Ends</span>
            <input
              className="input"
              type="date"
              value={endsOn}
              onChange={(e) => setEnds(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Show on</span>
            <select className="input" value={wallId} onChange={(e) => setWallId(e.target.value)}>
              {main.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.pages[0] ?? w.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Afterwards</span>
            <select className="input" disabled>
              <option>Back to {page?.name ?? "the normal wall"}</option>
            </select>
          </label>
        </div>
        {main.length === 0 && (
          <p className="error">
            Publish your normal wall first. A seasonal wall takes its place on the dates you pick.
          </p>
        )}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !wallId || !name.trim()}
            onClick={save}
          >
            {busy ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Seasons() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const cal = useQuery({
    queryKey: ["schedules", ws],
    queryFn: () => api<Calendar>(`/v1/w/${ws}/schedules`),
  });
  const walls = useQuery({
    queryKey: ["walls", ws],
    queryFn: () => api<{ walls: Wall[] }>(`/v1/w/${ws}/walls`),
  });
  const allowed = planAllows(workspace.plan, "seasons");

  // Three months from the start of this month.
  const span = useMemo(() => {
    if (!cal.data) return null;
    const t = cal.data.today;
    const from = parse(`${t.slice(0, 7)}-01`);
    const d = new Date(from);
    const to = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 3, 1, 12);
    const months = [0, 1, 2].map(
      (i) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + i, 1, 12)),
    );
    return { from, to, months, today: parse(t) };
  }, [cal.data]);

  const cancel = async (id: string) => {
    if (!window.confirm("Cancel this seasonal wall? Your normal wall stays on your site.")) return;
    try {
      const r = await patch<ActionResult>(`/v1/w/${ws}/schedules/${id}`, { cancel: true });
      toast(r.summary);
      qc.invalidateQueries({ queryKey: ["schedules", ws] });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  if (!cal.data || !span) return <p className="muted">Loading…</p>;
  const pct = (t: number) =>
    `${Math.max(0, Math.min(100, ((t - span.from) / (span.to - span.from)) * 100))}%`;
  const active = cal.data.schedules.filter((s) => s.status !== "cancelled");
  const rows = cal.data.pages.length ? cal.data.pages : [];

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row wrap">
        <div className="spacer">
          <h2 style={{ fontSize: 24 }}>Seasonal walls</h2>
          <p className="muted">
            Plan a wall for Halloween, a sale or a launch. It replaces your normal wall on its
            dates, then your normal wall comes back.
          </p>
        </div>
        {allowed ? (
          workspace.role !== "viewer" && (
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              + New seasonal wall
            </button>
          )
        ) : (
          <Link className="btn" to={`/w/${ws}/settings?tab=plan`}>
            Get seasonal walls on Starter
          </Link>
        )}
      </div>

      <section className="card" style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 640 }}>
          <div className="cal-head">
            <span />
            {span.months.map((m) => (
              <span key={m.toISOString()} className="label">
                {m.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" })}
              </span>
            ))}
          </div>
          {rows.length === 0 && (
            <p className="muted" style={{ padding: 10 }}>
              Publish a wall on your site first. Then you can plan seasonal walls for that page.
            </p>
          )}
          {rows.map((r) => (
            <div key={`${r.page}-${r.wallId}`} className="cal-row">
              <b>{r.page}</b>
              <div className="cal-track">
                <span className="cal-bar normal" style={{ left: 0, right: 0 }}>
                  {r.wallName}
                </span>
                {active
                  .filter((s) => s.replacesWallId === r.wallId)
                  .map((s) => (
                    <span
                      key={s.id}
                      className={`cal-bar season ${s.state}`}
                      style={{
                        left: pct(parse(s.startsOn)),
                        width: `calc(${pct(parse(s.endsOn) + DAY)} - ${pct(parse(s.startsOn))})`,
                      }}
                      title={`${s.wallName}, ${fmt(s.startsOn)} to ${fmt(s.endsOn)}`}
                    >
                      {s.wallName}
                    </span>
                  ))}
                <span className="cal-today" style={{ left: pct(span.today) }} aria-hidden="true" />
              </div>
            </div>
          ))}
        </div>
        {cal.data.overlaps.map((o) => (
          <p key={o.a + o.b} className="error" style={{ marginTop: 10 }}>
            {o.summary}
          </p>
        ))}
      </section>

      <div className="grid2">
        {active.map((s) => (
          <section key={s.id} className="card stack" style={{ gap: 8 }}>
            <div className="row">
              <h3 className="spacer" style={{ fontSize: 17 }}>
                {s.wallName}
              </h3>
              <span
                className={`status ${s.state === "live" ? "granted" : s.state === "waiting" ? "pending" : ""}`}
              >
                {s.state === "waiting"
                  ? "Waiting for approval"
                  : s.state[0]!.toUpperCase() + s.state.slice(1)}
              </span>
            </div>
            <p className="small muted">
              {fmt(s.startsOn)} to {fmt(s.endsOn)} · {s.page} · {s.postCount} posts · then back to{" "}
              {s.replacesWallName}
            </p>
            {s.createdBy && <p className="small muted">Planned by {s.createdBy}</p>}
            {workspace.role !== "viewer" && (
              <div className="row wrap">
                {s.approvalId && (
                  <Link className="btn ok sm" to={`/w/${ws}/ai#approvals`}>
                    Review
                  </Link>
                )}
                <Link className="btn secondary sm" to={`/w/${ws}/walls/${s.wallId}`}>
                  Edit wall
                </Link>
                {s.state !== "ended" && (
                  <button type="button" className="btn link small" onClick={() => cancel(s.id)}>
                    Cancel
                  </button>
                )}
              </div>
            )}
          </section>
        ))}
        {walls.data?.walls
          .filter((w) => w.kind === "normal" && w.status === "live")
          .map((w) => (
            <section key={w.id} className="card stack" style={{ gap: 8 }}>
              <h3 style={{ fontSize: 17 }}>{w.name}</h3>
              <p className="small muted">Always on · {w.pages.join(", ") || "your site"}</p>
            </section>
          ))}
      </div>
      {creating && (
        <NewSeason
          calendar={cal.data}
          walls={walls.data?.walls ?? []}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
